import { Card, Radio, Spin } from 'antd';
import { getFileUploadLogDailyStats } from 'apis/sys';
import { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import getClientTimeZone from 'utils/getClientTimeZone';

type StatsDays = 7 | 14 | 30;

type DailyPoint = {
  date: string;
  count: number;
  totalSize: number;
};

const DAYS_OPTIONS: { label: string; value: StatsDays }[] = [
  { label: '近7天', value: 7 },
  { label: '近2周', value: 14 },
  { label: '近1个月', value: 30 },
];

const formatFileSize = (bytes?: number | null) => {
  if (bytes == null) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const pickSizeUnit = (maxBytes: number) => {
  if (maxBytes >= 1024 * 1024 * 1024) {
    return { divisor: 1024 * 1024 * 1024, unit: 'GB' };
  }
  if (maxBytes >= 1024 * 1024) {
    return { divisor: 1024 * 1024, unit: 'MB' };
  }
  if (maxBytes >= 1024) {
    return { divisor: 1024, unit: 'KB' };
  }
  return { divisor: 1, unit: 'B' };
};

const FileUploadLogTrend = () => {
  const [days, setDays] = useState<StatsDays>(7);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<DailyPoint[]>([]);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      setLoading(true);
      try {
        const { list: nextList } = await getFileUploadLogDailyStats({
          days,
          timeZone: getClientTimeZone(),
        });
        if (!cancelled) {
          setList(nextList);
        }
      } catch (err) {
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      cancelled = true;
    };
  }, [days]);

  const totals = useMemo(() => {
    return list.reduce(
      (acc, item) => ({
        count: acc.count + item.count,
        totalSize: acc.totalSize + item.totalSize,
      }),
      { count: 0, totalSize: 0 },
    );
  }, [list]);

  const sizeUnit = useMemo(
    () => pickSizeUnit(Math.max(...list.map((item) => item.totalSize), 0)),
    [list],
  );

  const chartData = useMemo(
    () =>
      list.map((item) => ({
        ...item,
        dateLabel: item.date.slice(5),
        sizeValue: Number((item.totalSize / sizeUnit.divisor).toFixed(2)),
      })),
    [list, sizeUnit.divisor],
  );

  return (
    <Card
      bodyStyle={{ padding: '16px' }}
      title="上传趋势"
      extra={
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          value={days}
          onChange={(event) => setDays(event.target.value as StatsDays)}
          options={DAYS_OPTIONS}
        />
      }
    >
      <Spin spinning={loading}>
        <div className="mb-4 flex flex-wrap gap-8">
          <div>
            <div className="text-xs text-gray-400">合计数量</div>
            <div className="text-xl font-semibold text-gray-900">
              {totals.count.toLocaleString()} 个文件
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400">合计体积</div>
            <div className="text-xl font-semibold text-gray-900">
              {formatFileSize(totals.totalSize)}
            </div>
          </div>
        </div>

        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dateLabel" />
              <YAxis
                yAxisId="count"
                allowDecimals={false}
                tickFormatter={(value) => String(value)}
              />
              <YAxis
                yAxisId="size"
                orientation="right"
                tickFormatter={(value) => `${value} ${sizeUnit.unit}`}
              />
              <Tooltip
                formatter={(value, name, item) => {
                  if (item.dataKey === 'count') {
                    return [`${Number(value).toLocaleString()} 个`, '文件数量'];
                  }
                  return [formatFileSize(item.payload.totalSize), '总体积'];
                }}
                labelFormatter={(label, payload) =>
                  payload?.[0]?.payload?.date || label
                }
              />
              <Legend />
              <Line
                yAxisId="count"
                type="monotone"
                dataKey="count"
                name="文件数量"
                stroke="#1677ff"
                strokeWidth={2}
                dot={{ r: 3 }}
                isAnimationActive={false}
              />
              <Line
                yAxisId="size"
                type="monotone"
                dataKey="sizeValue"
                name="总体积"
                stroke="#fa8c16"
                strokeWidth={2}
                dot={{ r: 3 }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Spin>
    </Card>
  );
};

export default FileUploadLogTrend;
